import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'

import { getAuth, signOut } from 'firebase/auth'

import { Modal } from 'components/Modal'
import { PasswordCheckGate } from 'components/PasswordCheckGate'
import { Spinner } from 'components/Spinner'

import { setErrorAction } from 'root-redux/actions/common'
import { selectAppName } from 'root-redux/selects/common'
import {
  selectAuthToken,
  selectLearnworldsToken,
  selectUserContactEmail,
  selectUserCountry,
  selectUserSubscriptionInfo,
} from 'root-redux/selects/user'

import { growthBook, useGrowthBook } from 'hooks/useGrowthbook'
import { useUserData } from 'hooks/useUserData'
import { useWeb2WaveRedirect } from 'hooks/useWeb2WaveRedirect'

import { decodeRefEmail } from 'helpers/decodeRefEmail'

import { setIsModalShownAction } from 'modules/contactForm/redux'

import { goTo } from 'browser-history'
import { PageId } from 'page-constants'
import { PASSWORD_CHECK_APPS, REF_QUERY_PARAM } from 'root-constants'

export const AccountProvider: React.FC = () => {
  const dispatch = useDispatch()
  const { search } = useLocation()

  const userSubscriptionInfo = useSelector(selectUserSubscriptionInfo)
  const email = useSelector(selectUserContactEmail)
  const userCountryCode = useSelector(selectUserCountry)
  const appName = useSelector(selectAppName)
  const authToken = useSelector(selectAuthToken)
  const learnworldsToken = useSelector(selectLearnworldsToken)

  const refEmail = useMemo(
    () =>
      decodeRefEmail(
        new URLSearchParams(search.replace(/\+/g, encodeURIComponent('+'))).get(
          REF_QUERY_PARAM,
        ),
      ).email,
    [search],
  )

  // A ?ref= link proves nothing on its own for these apps.
  const hasPendingPasswordCheck =
    PASSWORD_CHECK_APPS.includes(appName) &&
    !!refEmail &&
    !learnworldsToken &&
    !authToken

  useUserData()
  useGrowthBook()

  const isWeb2WaveRedirectInProgress = useWeb2WaveRedirect()

  useEffect(() => {
    growthBook.setAttributes({
      ...growthBook.getAttributes(),
      country: userCountryCode,
      app: appName,
    })
  }, [appName, userCountryCode])

  const signOutFirebase = useCallback(() => signOut(getAuth()), [])

  const handleCloseModalWithoutSubscription = () => {
    dispatch(setErrorAction(''))

    signOutFirebase()

    dispatch(setIsModalShownAction(false))
    goTo(PageId.LOGIN)
  }

  if (isWeb2WaveRedirectInProgress) return <Spinner />

  if (hasPendingPasswordCheck) return <PasswordCheckGate email={refEmail} />

  return !userSubscriptionInfo || !email ? (
    <Modal onClose={handleCloseModalWithoutSubscription} hasContactSupport />
  ) : (
    <Outlet />
  )
}
